import { useEffect, useMemo, useState } from "react";
import { useBotsApi } from "./useBotsApi";
import BotForm from "./BotForm";


export default function BotsPanel() {
    const api = useBotsApi();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState("");
    const [editing, setEditing] = useState(null); // objeto bot a editar
    const [creating, setCreating] = useState(false);
    const [busy, setBusy] = useState(false);


    const filtered = useMemo(() => {
        const q = query.toLowerCase();
        return rows.filter(b => [b.name, b.kind, b.base_url].some(v => (v || "").toLowerCase().includes(q)));
    }, [rows, query]);


    const refresh = async () => {
        setLoading(true);
        try {
            const list = await api.list();
            setRows(list);
        } finally { setLoading(false); }
    };

    useEffect(() => { refresh(); }, []);


    const startCreate = () => { setCreating(true); setEditing(null); };
    const startEdit = (bot) => { setEditing(bot); setCreating(false); };


    const handleCreate = async (payload) => {
        setBusy(true);
        try {
            await api.create(payload);
            setCreating(false);
            await refresh();
        } finally { setBusy(false); }
    };


    const handleUpdate = async (payload) => {
        if (!editing) return;
        setBusy(true);
        try {
            await api.update(editing.id, payload);
            setEditing(null);
            await refresh();
        } finally { setBusy(false); }
    };

    const handleDelete = async (bot) => {
        if (!window.confirm(`¿Eliminar el bot "${bot.name}"?`)) return;
        setBusy(true);
        try {
            await api.remove(bot.id);
            await refresh();
        } finally { setBusy(false); }
    };


    const toggleActive = async (bot) => {
        setBusy(true);
        try {
            const updated = await api.toggleActive(bot.id, !bot.is_active);
            setRows(rows => rows.map(r => r.id === bot.id ? updated : r));
        } finally { setBusy(false); }
    };

    return (
        <section className="card section" style={{ maxWidth: 980 }}>
            <h3 style={{ marginTop: 0 }}>Bots</h3>


            {/* acciones */}
            <div className="card" style={{ padding: 12, marginBottom: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input className="input" placeholder="Buscar por nombre, tipo o URL" value={query} onChange={e => setQuery(e.target.value)} style={{ minWidth: 260 }} />
                <button className="btn" onClick={startCreate}>Nuevo bot</button>
                <button className="btn btn-muted" onClick={refresh} disabled={loading}>{loading ? "Cargando..." : "Recargar"}</button>
            </div>


            {/* formulario crear/editar */}
            {(creating || editing) && (
                <BotForm
                    value={editing}
                    busy={busy}
                    onSubmit={editing ? handleUpdate : handleCreate}
                    onCancel={() => { setCreating(false); setEditing(null); }}
                />
            )}

            {/* tabla de bots */}
            <div className="card" style={{ padding: 12 }}>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 2fr 1fr 1fr", gap: 8, fontWeight: 700, fontSize: 13 }}>
                    <div>Nombre</div>
                    <div>Tipo</div>
                    <div>Endpoint</div>
                    <div>Estado</div>
                    <div>Acciones</div>
                </div>
                <div style={{ height: 8 }} />
                {filtered.length === 0 && (
                    <div className="muted">{loading ? "Cargando..." : "No hay bots"}</div>
                )}
                <div style={{ display: "grid", gap: 8 }}>
                    {filtered.map(b => (
                        <div key={b.id} className="row" style={{ display: "grid", gridTemplateColumns: "2fr 1fr 2fr 1fr 1fr", gap: 8, alignItems: "center" }}>
                            <div>
                                <div style={{ fontWeight: 600 }}>{b.name}</div>
                                {b.kind !== "flow" && <div style={{ fontSize: 12, opacity: .7 }}>Webhook: {b.webhook_path || "/"}</div>}
                            </div>
                            <div>{b.kind === "flow" ? "Visual Flow" : b.kind}</div>
                            <div style={{ fontSize: 12, wordBreak: "break-all" }}>
                                {b.kind === "flow" ? `Flujo: ${b.flow_id || "—"}` : (b.base_url || "—")}
                            </div>
                            <div>
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                                    <span style={{ width: 10, height: 10, borderRadius: "50%", background: b.is_active ? "#22c55e" : "#ef4444", border: "2px solid #0f172a" }} />
                                    {b.is_active ? "Activo" : "Inactivo"}
                                </span>
                            </div>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                <button className="btn btn-muted" onClick={() => startEdit(b)}>Editar</button>
                                <button className="btn" onClick={() => toggleActive(b)}>{b.is_active ? "Desactivar" : "Activar"}</button>
                                <button className="btn btn-warn" onClick={() => handleDelete(b)}>Borrar</button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 8 }}>
                Nota: si una conexión específica no tiene bot activo, se usará el bot asignado al canal.
            </div>
        </section>
    );
}